/**
 * Bank List Component
 * 
 * Displays a list of banks.
 */

import * as React from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, RefreshCw, Building2, Pencil, Trash2 } from "lucide-react";
import type { Bank } from "@/services/bankService";

interface BankListProps {
  banks: Bank[];
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
  onEdit?: (bank: Bank) => void;
  onDelete?: (bank: Bank) => void;
}

export function BankList({ banks, loading, error, onRefresh, onEdit, onDelete }: BankListProps) {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-32 gap-2">
        <p className="text-sm text-destructive">{error}</p>
        <Button variant="outline" size="sm" onClick={onRefresh}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Coba Lagi
        </Button>
      </div>
    );
  }

  if (banks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-32 gap-2 text-muted-foreground">
        <Building2 className="h-8 w-8" />
        <p className="text-sm">Belum ada bank yang terdaftar</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Total: {banks.length} bank
        </p>
        <Button variant="outline" size="sm" onClick={onRefresh}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nama Bank</TableHead>
              <TableHead>Kode</TableHead>
              <TableHead>Deskripsi</TableHead>
              <TableHead>Status</TableHead>
              {(onEdit || onDelete) && <TableHead className="w-[100px]">Aksi</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {banks.map((bank) => (
              <TableRow key={bank._id}>
                <TableCell className="font-medium">{bank.name}</TableCell>
                <TableCell>
                  <Badge variant="outline">{bank.code}</Badge>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {bank.description || "-"}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Badge 
                      variant={
                        bank.status === "active" 
                          ? "default" 
                          : bank.status === "pending" 
                          ? "secondary" 
                          : "outline"
                      }
                      className="capitalize"
                    >
                      {bank.status === "active" 
                        ? "Active" 
                        : bank.status === "pending" 
                        ? "Pending" 
                        : bank.status === "inactive"
                        ? "Inactive"
                        : "Pending"}
                    </Badge>
                  </div>
                </TableCell>
                {(onEdit || onDelete) && (
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {onEdit && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onEdit(bank)}
                          className="h-8 w-8"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      {onDelete && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onDelete(bank)}
                          className="h-8 w-8 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

